import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FiArrowDown, FiArrowUp, FiChevronDown, FiExternalLink, FiRefreshCw, FiSearch } from 'react-icons/fi';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import i18n from '../../../i18n';
import CompositorEnvio, { AUDIENCIAS } from './notificaciones/CompositorEnvio';
import Paginacion from '../../../components/ui/Paginacion';
import 'react-toastify/dist/ReactToastify.css';
import '../../../css/notificaciones-admin.css';

const TIPOS = ['sistema', 'ticket', 'proyecto', 'usuario'];
const POR_PAGINA = 15;

const fechaCompleta = (fecha) => new Intl.DateTimeFormat(i18n.language, {
  day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(fecha));

const fechaRelativa = (fecha) => {
  const minutos = Math.floor((Date.now() - new Date(fecha).getTime()) / 60000);
  if (minutos < 1) return i18n.t('gestorNotificaciones.ahora');
  if (minutos < 60) return i18n.t('gestorNotificaciones.haceMin', { n: minutos });
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return i18n.t('gestorNotificaciones.haceHoras', { n: horas });
  const dias = Math.floor(horas / 24);
  if (dias < 30) return i18n.t('gestorNotificaciones.haceDias', { n: dias });
  return new Date(fecha).toLocaleDateString(i18n.language);
};

/**
 * Historial de avisos enviados a la campana del panel y alta de envíos nuevos.
 * Solo administradores. El compositor vive en su propio componente.
 */
const NotificacionesManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = localStorage.getItem('AuthToken');
  const esAdmin = token ? jwtDecode(token).role === 'admin' : false;

  const [componiendo, setComponiendo] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [filtroAudiencia, setFiltroAudiencia] = useState('todas');
  const [filtroLectura, setFiltroLectura] = useState('todas');
  const [orden, setOrden] = useState('desc');
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [expandidas, setExpandidas] = useState([]);
  const [eliminando, setEliminando] = useState(false);
  const [pagina, setPagina] = useState(1);

  useEffect(() => { if (!esAdmin) navigate('/'); }, [esAdmin, navigate]);

  const [consultaNotificaciones, consultaUsuarios, consultaEstadisticas] = useQueries({
    queries: [
      {
        queryKey: claves.notificacionesAdmin(),
        queryFn: async () => (await axios.get(`${BASE_URL}/notificaciones-admin/todas?limite=500`, cabecerasAuth())).data.notificaciones || [],
        enabled: esAdmin,
      },
      {
        queryKey: claves.usuariosNotificables(),
        queryFn: async () => (await axios.get(`${BASE_URL}/notificaciones-admin/usuarios`, cabecerasAuth())).data.usuarios || [],
        enabled: esAdmin, staleTime: 5 * 60_000,
      },
      {
        queryKey: claves.estadisticasNotificaciones(),
        queryFn: async () => (await axios.get(`${BASE_URL}/notificaciones-admin/estadisticas`, cabecerasAuth())).data.estadisticas,
        enabled: esAdmin,
      },
    ],
  });

  useEffect(() => {
    if (consultaNotificaciones.isError || consultaUsuarios.isError || consultaEstadisticas.isError) {
      toast.error(t('gestorNotificaciones.errorCargarDatos'));
    }
  }, [consultaNotificaciones.isError, consultaUsuarios.isError, consultaEstadisticas.isError, t]);

  const notificaciones = useMemo(() => consultaNotificaciones.data || [], [consultaNotificaciones.data]);
  const usuarios = useMemo(() => consultaUsuarios.data || [], [consultaUsuarios.data]);
  const estadisticas = consultaEstadisticas.data;
  const refrescando = consultaNotificaciones.isFetching && !consultaNotificaciones.isPending;

  const recargar = () => {
    setSeleccionadas([]);
    queryClient.invalidateQueries({ queryKey: claves.notificacionesAdmin() });
    queryClient.invalidateQueries({ queryKey: claves.estadisticasNotificaciones() });
  };

  const filtradas = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return [...notificaciones].filter((n) => {
      const coincideTexto = !texto || `${n.titulo} ${n.mensaje} ${n.usuario_destinatario || ''}`.toLowerCase().includes(texto);
      const coincideTipo = filtroTipo === 'todas' || n.tipo === filtroTipo;
      const coincideAudiencia = filtroAudiencia === 'todas' || n.audiencia === filtroAudiencia;
      const vistas = Number(n.estadisticas?.vistas || 0);
      const total = Number(n.estadisticas?.destinatarios || 0);
      const completa = total > 0 && vistas >= total;
      const coincideLectura = filtroLectura === 'todas' || (filtroLectura === 'completas' ? completa : !completa);
      return coincideTexto && coincideTipo && coincideAudiencia && coincideLectura;
    }).sort((a, b) => (orden === 'desc' ? 1 : -1) * (new Date(b.createdAt) - new Date(a.createdAt)));
  }, [notificaciones, busqueda, filtroTipo, filtroAudiencia, filtroLectura, orden]);

  const hayFiltros = busqueda.trim() || filtroTipo !== 'todas' || filtroAudiencia !== 'todas' || filtroLectura !== 'todas';
  const limpiarFiltros = () => { setBusqueda(''); setFiltroTipo('todas'); setFiltroAudiencia('todas'); setFiltroLectura('todas'); };

  useEffect(() => { setPagina(1); setSeleccionadas([]); }, [busqueda, filtroTipo, filtroAudiencia, filtroLectura, orden]);
  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const actuales = filtradas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  useEffect(() => { if (pagina > totalPaginas) setPagina(totalPaginas); }, [pagina, totalPaginas]);

  const eliminarUna = async (id) => {
    if (!window.confirm(t('gestorNotificaciones.confirmarEliminarUna'))) return;
    try {
      await axios.delete(`${BASE_URL}/notificaciones/${id}`, cabecerasAuth());
      toast.success(t('gestorNotificaciones.notificacionEliminada')); recargar();
    } catch (_) { toast.error(t('gestorNotificaciones.errorEliminar')); }
  };

  const eliminarSeleccionadas = async () => {
    if (!seleccionadas.length || !window.confirm(t('gestorNotificaciones.confirmarEliminarVarias', { count: seleccionadas.length }))) return;
    setEliminando(true);
    const resultados = await Promise.allSettled(seleccionadas.map((id) => axios.delete(`${BASE_URL}/notificaciones/${id}`, cabecerasAuth())));
    const fallidas = resultados.filter((r) => r.status === 'rejected').length;
    if (fallidas) toast.error(t('gestorNotificaciones.eliminacionParcial', { eliminadas: resultados.length - fallidas, fallidas }));
    else toast.success(t('gestorNotificaciones.notificacionesEliminadas'));
    setEliminando(false); recargar();
  };

  const todasPagina = actuales.length > 0 && actuales.every((n) => seleccionadas.includes(n.id));
  const seleccionarPagina = () => setSeleccionadas((prev) => todasPagina
    ? prev.filter((id) => !actuales.some((n) => n.id === id))
    : [...new Set([...prev, ...actuales.map((n) => n.id)])]);
  const alternar = (lista, setLista, id) => setLista(lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);

  const abrirEnlace = (notificacion) => {
    if (!notificacion.link) return;
    if (notificacion.link.startsWith('/')) navigate(notificacion.link);
    else window.open(notificacion.link, '_blank', 'noopener,noreferrer');
  };

  if (consultaNotificaciones.isPending) return <div className="loader-fullscreen"><Loader /></div>;

  const resumen = estadisticas ? [
    [estadisticas.totalNotificaciones, t('gestorNotificaciones.nt.resumenEnvios')],
    [estadisticas.notificacionesMasivas, t('gestorNotificaciones.nt.resumenMasivos')],
    [estadisticas.individualesNoLeidas, t('gestorNotificaciones.nt.resumenSinLeer')],
    [`${estadisticas.porcentajeLeidasIndividuales}%`, t('gestorNotificaciones.nt.resumenLeidas')],
  ] : [];

  return (
    <main className="site-width ntf">
      <ToastContainer position="bottom-right" autoClose={3500} />

      <div className="ntf-cabecera">
        <div>
          <h1>{t('gestorNotificaciones.nt.paginaTitulo')}</h1>
          <p>{t('gestorNotificaciones.nt.subtitulo')}</p>
        </div>
        <div className="ntf-cabecera-acciones">
          <button type="button" className="ntf-boton-icono" onClick={recargar} disabled={refrescando} aria-label={t('gestorNotificaciones.actualizar')} title={t('gestorNotificaciones.actualizar')}>
            <FiRefreshCw className={refrescando ? 'ntf-girando' : undefined} />
          </button>
          <button type="button" className="ntf-boton" onClick={() => setComponiendo(true)}>
            {t('gestorNotificaciones.nuevoEnvio')}
          </button>
        </div>
      </div>

      {resumen.length > 0 && (
        <dl className="ntf-resumen" aria-label={t('gestorNotificaciones.resumen')}>
          {resumen.map(([valor, etiqueta]) => (
            <div key={etiqueta}><dd>{valor}</dd><dt>{etiqueta}</dt></div>
          ))}
        </dl>
      )}

      <section className="ntf-historial" aria-labelledby="ntf-historial-titulo">
        <div className="ntf-historial-cabecera">
          <h2 id="ntf-historial-titulo">{t('gestorNotificaciones.historialEnvios')}</h2>
          <span>{t('gestorNotificaciones.nt.envios', { count: filtradas.length })}</span>
        </div>

        <div className="ntf-barra">
          {seleccionadas.length ? (
            <div className="ntf-seleccion" role="status">
              <span>{t('gestorNotificaciones.seleccionadas', { count: seleccionadas.length })}</span>
              <button type="button" className="ntf-boton-secundario" onClick={() => setSeleccionadas([])}>{t('comun.cancelar')}</button>
              <button type="button" className="ntf-boton-peligro" onClick={eliminarSeleccionadas} disabled={eliminando}>
                {eliminando ? t('gestorNotificaciones.eliminando') : t('gestorNotificaciones.eliminarSeleccionadas')}
              </button>
            </div>
          ) : (
            <>
              <label className="ntf-buscar">
                <FiSearch aria-hidden="true" />
                <span className="sr-only">{t('comun.buscar')}</span>
                <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder={t('gestorNotificaciones.buscarPorTituloMensaje')} />
              </label>
              <select aria-label={t('gestorNotificaciones.tipoNotificacion')} value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="todas">{t('gestorNotificaciones.todosLosTipos')}</option>
                {TIPOS.map((v) => <option key={v} value={v}>{t(`gestorNotificaciones.tipos.${v}`)}</option>)}
              </select>
              <select aria-label={t('gestorNotificaciones.filtroAudiencia')} value={filtroAudiencia} onChange={(e) => setFiltroAudiencia(e.target.value)}>
                <option value="todas">{t('gestorNotificaciones.todasAudiencias')}</option>
                {AUDIENCIAS.map((v) => <option key={v} value={v}>{t(`gestorNotificaciones.destinatarios.${v}`)}</option>)}
              </select>
              <select aria-label={t('gestorNotificaciones.filtroLectura')} value={filtroLectura} onChange={(e) => setFiltroLectura(e.target.value)}>
                <option value="todas">{t('gestorNotificaciones.todasLecturas')}</option>
                <option value="pendientes">{t('gestorNotificaciones.conLecturasPendientes')}</option>
                <option value="completas">{t('gestorNotificaciones.lecturaCompleta')}</option>
              </select>
              <button type="button" className="ntf-orden" onClick={() => setOrden((v) => (v === 'desc' ? 'asc' : 'desc'))}>
                {orden === 'desc' ? <FiArrowDown aria-hidden="true" /> : <FiArrowUp aria-hidden="true" />}
                {orden === 'desc' ? t('gestorNotificaciones.masRecientesPrimero') : t('gestorNotificaciones.masAntiguasPrimero')}
              </button>
            </>
          )}
        </div>

        {actuales.length > 0 ? (
          <>
            <div className="ntf-lista-cabecera">
              <label>
                <input type="checkbox" checked={todasPagina} onChange={seleccionarPagina} />
                {t('gestorNotificaciones.seleccionarPagina')}
              </label>
              <span className="ntf-col-audiencia">{t('gestorNotificaciones.audiencia')}</span>
              <span className="ntf-col-lectura">{t('gestorNotificaciones.lectura')}</span>
              <span className="ntf-col-fecha">{t('gestorNotificaciones.nt.enviada')}</span>
            </div>

            <ul className="ntf-lista">
              {actuales.map((n) => {
                const abierta = expandidas.includes(n.id);
                const vistas = Number(n.estadisticas?.vistas || 0);
                const total = Number(n.estadisticas?.destinatarios || 0);
                const porcentaje = total ? Math.round((vistas / total) * 100) : 0;
                const destacada = n.prioridad === 'alta' || n.prioridad === 'urgente';
                return (
                  <li key={n.id} className={`ntf-fila${abierta ? ' abierta' : ''}${seleccionadas.includes(n.id) ? ' marcada' : ''}`}>
                    <div className="ntf-fila-principal">
                      <input
                        type="checkbox"
                        checked={seleccionadas.includes(n.id)}
                        onChange={() => alternar(seleccionadas, setSeleccionadas, n.id)}
                        aria-label={t('gestorNotificaciones.seleccionarNotificacion')}
                      />
                      <button type="button" className="ntf-fila-boton" aria-expanded={abierta} onClick={() => alternar(expandidas, setExpandidas, n.id)}>
                        <span className="ntf-fila-texto">
                          <strong>
                            {destacada && <em className={`ntf-prioridad ntf-prioridad--${n.prioridad}`}>{t(`gestorNotificaciones.prioridades.${n.prioridad}`)}</em>}
                            {n.titulo}
                          </strong>
                          <span>{n.mensaje}</span>
                        </span>
                        <span className="ntf-col-audiencia">
                          {n.audiencia === 'especifico' && n.usuario_destinatario
                            ? `@${n.usuario_destinatario}`
                            : t(`gestorNotificaciones.destinatarios.${n.audiencia || 'todos'}`)}
                        </span>
                        <span className="ntf-col-lectura">
                          <span className="ntf-medidor" aria-hidden="true"><i style={{ width: `${porcentaje}%` }} /></span>
                          <span>{t('gestorNotificaciones.nt.leidaPor', { vistas, total })}</span>
                        </span>
                        <time className="ntf-col-fecha" dateTime={n.createdAt} title={fechaCompleta(n.createdAt)}>{fechaRelativa(n.createdAt)}</time>
                        <FiChevronDown className="ntf-flecha" aria-hidden="true" />
                      </button>
                    </div>

                    {abierta && (
                      <div className="ntf-detalle">
                        <p className="ntf-detalle-mensaje">{n.mensaje}</p>
                        <dl>
                          <div><dt>{t('gestorNotificaciones.alcance')}</dt><dd>{t('gestorNotificaciones.nt.personas', { count: total })}</dd></div>
                          <div><dt>{t('gestorNotificaciones.lectura')}</dt><dd>{porcentaje}%</dd></div>
                          <div><dt>{t('gestorNotificaciones.nt.tipo')}</dt><dd>{t(`gestorNotificaciones.tipos.${n.tipo}`, { defaultValue: n.tipo })}</dd></div>
                          <div><dt>{t('gestorNotificaciones.enviadoPor')}</dt><dd>@{n.enviado_por || t('gestorNotificaciones.sistema')}</dd></div>
                          <div><dt>{t('gestorNotificaciones.fechaEnvio')}</dt><dd>{fechaCompleta(n.createdAt)}</dd></div>
                        </dl>
                        <div className="ntf-detalle-acciones">
                          {n.link && (
                            <button type="button" className="ntf-enlace" onClick={() => abrirEnlace(n)}>
                              {n.link} <FiExternalLink aria-hidden="true" />
                            </button>
                          )}
                          <button type="button" className="ntf-enlace ntf-enlace--peligro" onClick={() => eliminarUna(n.id)}>
                            {t('gestorNotificaciones.eliminarNotificacion')}
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <Paginacion
              pagina={pagina}
              porPagina={POR_PAGINA}
              total={filtradas.length}
              onCambiar={setPagina}
              etiqueta={t('gestorNotificaciones.paginacion')}
              compacta
            />
          </>
        ) : (
          <div className="ntf-vacio">
            {hayFiltros ? (
              <>
                <p>{t('gestorNotificaciones.nt.sinResultados')}</p>
                <button type="button" className="ntf-boton-secundario" onClick={limpiarFiltros}>{t('gestorNotificaciones.limpiarFiltros')}</button>
              </>
            ) : (
              <>
                <p>{t('gestorNotificaciones.nt.sinEnvios')}</p>
                <button type="button" className="ntf-boton" onClick={() => setComponiendo(true)}>{t('gestorNotificaciones.nuevoEnvio')}</button>
              </>
            )}
          </div>
        )}
      </section>

      {componiendo && (
        <CompositorEnvio
          usuarios={usuarios}
          onCerrar={() => setComponiendo(false)}
          onEnviado={() => { setComponiendo(false); recargar(); }}
        />
      )}
    </main>
  );
};

export default NotificacionesManager;
